import axios from '../config/axios';
import { authService } from './authService';

export interface SystemPrompt {
  id: number;
  prompt: string;
  model_name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  organization_id?: number;
  user_id?: number;
}

export interface CreatePromptData {
  prompt: string;
  model_name: string;
  description: string;
}

export interface UpdatePromptData {
  prompt?: string;
  model_name?: string;
  description?: string;
  is_active?: boolean;
}

class PromptService {
  private getAuthHeaders() {
    const token = authService.getToken();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // ===== ORGANIZATION PROMPTS =====

  // List all organization prompts (organization inferred from JWT)
  async getOrganizationPrompts(): Promise<SystemPrompt[]> {
    try {
      const response = await axios.get(
        `/api/scribing/system-prompts/organization`,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return Array.isArray(response.data) ? response.data : [response.data];
    } catch (error) {
      console.error('Error fetching organization prompts:', error);
      throw error;
    }
  }

  // Get single organization prompt
  async getOrganizationPrompt(promptId: number): Promise<SystemPrompt> {
    try {
      const response = await axios.get(
        `/api/scribing/system-prompts/organization/prompt/${promptId}`,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching organization prompt:', error);
      throw error;
    }
  }

  // Create organization prompt
  async createOrganizationPrompt(promptData: CreatePromptData): Promise<SystemPrompt> {
    try {
      const response = await axios.post(
        `/api/scribing/system-prompts/organization`,
        promptData,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating organization prompt:', error);
      throw error;
    }
  }

  // Update organization prompt
  async updateOrganizationPrompt(
    promptId: number,
    promptData: UpdatePromptData
  ): Promise<SystemPrompt> {
    try {
      const response = await axios.patch(
        `/api/scribing/system-prompts/organization/prompt/${promptId}`,
        promptData,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating organization prompt:', error);
      throw error;
    }
  }

  // Delete organization prompt
  async deleteOrganizationPrompt(promptId: number): Promise<void> {
    try {
      await axios.delete(
        `/api/scribing/system-prompts/organization/prompt/${promptId}`,
        {
          headers: this.getAuthHeaders(),
        }
      );
    } catch (error) {
      console.error('Error deleting organization prompt:', error);
      throw error;
    }
  }

  // ===== USER PROMPTS =====

  // List all user prompts
  async getUserPrompts(): Promise<SystemPrompt[]> {
    try {
      const response = await axios.get('/api/scribing/system-prompts/user', {
        headers: this.getAuthHeaders(),
      });
      return Array.isArray(response.data) ? response.data : [response.data];
    } catch (error) {
      console.error('Error fetching user prompts:', error);
      throw error;
    }
  }

  // Get single user prompt
  async getUserPrompt(promptId: number): Promise<SystemPrompt> {
    try {
      const response = await axios.get(
        `/api/scribing/system-prompts/user/${promptId}`,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user prompt:', error);
      throw error;
    }
  }

  // Create user prompt
  async createUserPrompt(promptData: CreatePromptData): Promise<SystemPrompt> {
    try {
      const response = await axios.post(
        '/api/scribing/system-prompts/user',
        promptData,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating user prompt:', error);
      throw error;
    }
  }

  // Update user prompt
  async updateUserPrompt(promptId: number, promptData: UpdatePromptData): Promise<SystemPrompt> {
    try {
      const response = await axios.patch(
        `/api/scribing/system-prompts/user/${promptId}`,
        promptData,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating user prompt:', error);
      throw error;
    }
  }

  // Delete user prompt
  async deleteUserPrompt(promptId: number): Promise<void> {
    try {
      await axios.delete(`/api/scribing/system-prompts/user/${promptId}`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error deleting user prompt:', error);
      throw error;
    }
  }

  // ===== GLOBAL PROMPTS (Super Admin Only) =====

  // List all global prompts
  async getGlobalPrompts(): Promise<SystemPrompt[]> {
    try {
      const response = await axios.get('/api/scribing/system-prompts/global', {
        headers: this.getAuthHeaders(),
      });
      return Array.isArray(response.data) ? response.data : [response.data];
    } catch (error) {
      console.error('Error fetching global prompts:', error);
      throw error;
    }
  }

  // Get single global prompt
  async getGlobalPrompt(promptId: number): Promise<SystemPrompt> {
    try {
      const response = await axios.get(
        `/api/scribing/system-prompts/global/${promptId}`,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching global prompt:', error);
      throw error;
    }
  }

  // Create global prompt
  async createGlobalPrompt(promptData: CreatePromptData): Promise<SystemPrompt> {
    try {
      const response = await axios.post(
        '/api/scribing/system-prompts/global',
        promptData,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating global prompt:', error);
      throw error;
    }
  }

  // Update global prompt
  async updateGlobalPrompt(promptId: number, promptData: UpdatePromptData): Promise<SystemPrompt> {
    try {
      const response = await axios.patch(
        `/api/scribing/system-prompts/global/${promptId}`,
        promptData,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating global prompt:', error);
      throw error;
    }
  }

  // Delete global prompt
  async deleteGlobalPrompt(promptId: number): Promise<void> {
    try {
      await axios.delete(`/api/scribing/system-prompts/global/${promptId}`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error deleting global prompt:', error);
      throw error;
    }
  }
}

export const promptService = new PromptService();
